import { CommonModule } from '@angular/common';
import { Component, NgZone, OnInit, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatFormFieldModule } from '@angular/material/form-field';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { EMPTY, catchError, from, switchMap, tap } from 'rxjs';
import { ApiService, PaymentCheckout, PaymentVerificationInput, Subscription, SubscriptionPlan } from 'src/app/core/services/api.service';
import { IfieldsFieldStyle, IfieldsKeyPressData, IfieldsService, IfieldsVerification } from 'src/app/core/services/ifields.service';
import { environment } from 'src/environments/environment';
import { AuthService } from '@auth0/auth0-angular';
import { SubscriptionEntitlementService } from 'src/app/core/services/subscription-entitlement.service';
import { QuickTourOnboardingService } from 'src/app/core/tutorial/quick-tour-onboarding.service';

@Component({
  selector: 'app-subscription-checkout',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatButtonModule, MatCheckboxModule, MatDialogModule, MatIconModule, MatInputModule, MatProgressSpinnerModule, MatFormFieldModule],
  templateUrl: './subscription-checkout.component.html',
  styleUrls: ['./subscription-checkout.component.scss']
})
export class SubscriptionCheckoutComponent implements OnInit {
  private readonly api = inject(ApiService);
  private readonly ifields = inject(IfieldsService);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly dialogRef = inject(MatDialogRef<SubscriptionCheckoutComponent>);
  private readonly formBuilder = inject(FormBuilder);
  private readonly zone = inject(NgZone);
  private readonly auth = inject(AuthService);
  private readonly entitlement = inject(SubscriptionEntitlementService);
  private readonly onboarding = inject(QuickTourOnboardingService);
  private readonly dialogData = inject<{ requiredActivation?: boolean } | null>(MAT_DIALOG_DATA, { optional: true });

  readonly form = this.formBuilder.group({
    expiry: ['', [Validators.required, Validators.pattern(/^\d{2}\/\d{2}$/)]],
    firstName: ['', Validators.required],
    lastName: ['', Validators.required],
    street: ['', Validators.required],
    city: ['', Validators.required],
    country: ['', Validators.required],
    state: [''],
    zip: ['', Validators.required],
    mobile: [''],
    termsAccepted: [false, Validators.requiredTrue]
  });
  plan: SubscriptionPlan | null = null;
  subscription: Subscription | null = null;
  trialEligible = false;
  trialEligibilityLoaded = false;
  loadingPlan = true;
  loading = false;
  ifieldsReady = false;
  checkoutApproved = false;
  errorMessage = '';
  cardNumberHasValue = false;
  cvvHasValue = false;
  private checkoutId: string | null = null;
  readonly iframeUrl = `https://cdn.cardknox.com/ifields/${environment.ifields.version}/ifield.htm`;
  readonly safeIframeUrl: SafeResourceUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.iframeUrl);
  private readonly compactIfieldsStyle: IfieldsFieldStyle = {
    border: '0',
    background: 'transparent',
    color: '#6f879a',
    'font-size': '12px',
    height: '40px',
    'line-height': '40px',
    padding: '0 12px',
    width: '100%',
    'box-sizing': 'border-box'
  };

  get requiredActivation(): boolean {
    return this.dialogData?.requiredActivation === true;
  }

  ngOnInit(): void {
    this.loadSubscription();
    this.ifields.loadScript(environment.ifields.scriptUrl)
      .then(() => {
        this.ifields.initialize(
          environment.ifields.publicKey,
          verification => this.verify3ds(verification),
          this.compactIfieldsStyle
        );
        this.ifields.addKeyPressCallback(data => this.updateSecureFieldState(data));
        this.ifieldsReady = true;
      })
      .catch(() => this.errorMessage = 'Secure payment fields could not be loaded.');
  }

  get price(): string {
    return this.plan ? (this.plan.priceCents / 100).toFixed(0) : '';
  }

  get cadence(): string {
    if (!this.plan) return '';
    return this.plan.interval.count === 1 ? this.plan.interval.unit : `${this.plan.interval.count} ${this.plan.interval.unit}s`;
  }

  get renewalDate(): string | null {
    const value = this.subscription?.nextRenewalAt;
    if (!value) return null;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : new Intl.DateTimeFormat('en', { day: 'numeric', month: 'short', year: 'numeric' }).format(date);
  }

  get trialEndDate(): string {
    const value = this.subscription?.nextRenewalAt;
    const date = value ? new Date(value) : new Date(Date.now() + 3 * 24 * 60 * 60 * 1000);
    return Number.isNaN(date.getTime()) ? '' : new Intl.DateTimeFormat('en', { day: 'numeric', month: 'short', year: 'numeric' }).format(date);
  }

  get nextRenewalDate(): string {
    return this.renewalDate ?? 'Not reported';
  }

  get activationTitle(): string {
    return this.trialEligible ? 'Start your<br>3-day free trial.' : 'Activate your subscription.';
  }

  get todayChargeLabel(): string {
    return this.trialEligible ? '$0 today.' : `$${this.price} charged today.`;
  }

  get chargeTimingLabel(): string {
    return this.trialEligible ? `No charge until ${this.trialEndDate}.` : `Your subscription starts today.`;
  }

  get submitLabel(): string {
    return this.trialEligible ? 'Start my 3-day free trial' : 'Start subscription';
  }

  get formIntroLabel(): string {
    return this.trialEligible ? 'Add your billing details to start your 3-day free trial.' : 'Add your billing details to activate your subscription.';
  }

  submit(): void {
    if (!this.plan || this.subscription || this.loading) return;
    if (!this.trialEligibilityLoaded) {
      this.errorMessage = 'Subscription eligibility is still loading.';
      return;
    }
    if (!this.ifieldsReady) {
      this.errorMessage = 'Secure payment fields are still loading.';
      return;
    }
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.errorMessage = 'Complete the required payment details and accept the billing terms.';
      return;
    }
    this.loading = true;
    this.errorMessage = '';
    this.api.createPaymentCheckout({ type: 'subscription_start', planCode: this.plan.code }).pipe(
      tap(checkout => this.checkoutId = checkout.id),
      switchMap(checkout => from(this.ifields.getTokens()).pipe(
        switchMap(tokens => this.api.attemptPaymentCheckout(checkout.id, {
          ...tokens,
          expiry: this.form.controls.expiry.value!.replace('/', ''),
          billing: this.billingDetails()
        }))
      )),
      catchError(error => {
        this.loading = false;
        this.errorMessage = error?.status === 409 ? 'You already have an active subscription or a checkout in progress.' : 'We could not start the subscription. No payment was completed.';
        return EMPTY;
      })
    ).subscribe(result => this.handleCheckoutResult(result));
  }

  close(): void {
    this.dialogRef.close(this.checkoutApproved);
  }

  signOut(): void {
    this.auth.logout({ logoutParams: { returnTo: window.location.origin } });
  }

  returnToWorkspace(): void {
    this.dialogRef.close(true);
    this.onboarding.refresh();
  }

  normalizeExpiry(value: string): void {
    const digits = value.replace(/\D/g, '').slice(0, 4);
    this.form.controls.expiry.setValue(digits.length > 2 ? `${digits.slice(0, 2)}/${digits.slice(2)}` : digits);
  }

  private loadSubscription(): void {
    this.api.getSubscriptionPlans().subscribe({
      next: result => {
        this.plan = result.plans.find(plan => plan.entitlement?.['monthlySubscription']) ?? result.plans[0] ?? null;
        this.loadingPlan = false;
        if (!this.plan) this.errorMessage = 'Subscription is not currently available.';
      },
      error: () => { this.loadingPlan = false; this.errorMessage = 'Subscription details are temporarily unavailable.'; }
    });
    this.api.getMySubscription().subscribe({
      next: result => {
        this.subscription = result.subscription;
        this.trialEligible = result.trialEligible ?? !result.trialConsumed;
        this.trialEligibilityLoaded = true;
      },
      error: () => { this.errorMessage = 'Subscription eligibility is temporarily unavailable.'; }
    });
  }

  private handleCheckoutResult(result: PaymentCheckout): void {
    if (result.status === 'requires3ds' && result.transaction && this.checkoutId) {
      try { this.ifields.verify3DS(result.transaction); } catch { this.loading = false; this.errorMessage = '3D Secure verification could not be started.'; }
      return;
    }
    this.loading = false;
    if (result.status !== 'approved') {
      this.errorMessage = result.reason ? `Payment declined: ${result.reason}` : 'Payment was declined. No subscription was started.';
      return;
    }
    this.confirmApprovedSubscription();
  }

  private verify3ds(verification: IfieldsVerification): void {
    if (!this.checkoutId) return;
    this.api.verifyPaymentCheckout3ds(this.checkoutId, verification as PaymentVerificationInput).subscribe({
      next: result => this.handleCheckoutResult(result),
      error: () => { this.loading = false; this.errorMessage = '3D Secure verification was not approved. No subscription was started.'; }
    });
  }

  private confirmApprovedSubscription(): void {
    this.entitlement.refresh();
    this.checkoutApproved = true;
    this.dialogRef.updateSize('min(704px, calc(100vw - 32px))');
    this.api.getMySubscription().subscribe({
      next: result => this.subscription = result.subscription,
      error: () => undefined
    });
  }

  private updateSecureFieldState(data: IfieldsKeyPressData): void {
    this.zone.run(() => {
      this.cardNumberHasValue = (data.cardNumberFormattedLength ?? 0) > 0;
      this.cvvHasValue = (data.cvvLength ?? 0) > 0;
    });
  }

  private billingDetails(): Record<string, string> {
    const value = this.form.getRawValue();
    return {
      xBillFirstName: value.firstName || '', xBillLastName: value.lastName || '',
      xBillStreet: value.street || '', xBillCity: value.city || '', xBillState: value.state || '',
      xBillZip: value.zip || '', xBillCountry: value.country || '', xBillMobile: value.mobile || ''
    };
  }
}
